import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Send, Sparkles } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageList } from "@/components/sg/MessageList";

const STARTERS = [
  "How should I price a 200 sq ft bathroom tile job?",
  "Write a polite but firm message to a client who hasn't approved a milestone in 3 days.",
  "Draft a change order for adding recessed lighting to a kitchen remodel.",
];

function ConversationList({ conversations, cid, onSelect, onNew, onDelete }) {
  return (
    <div className="md:col-span-4 border border-border rounded-xl bg-card p-3 md:h-[70vh] overflow-y-auto sg-scroll">
      <div className="flex items-center justify-between px-2 py-1 mb-2">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Conversations</div>
        <Button data-testid="chat-new-btn" size="sm" variant="ghost" onClick={onNew}><Plus className="h-4 w-4 mr-1" />New</Button>
      </div>
      {conversations.length === 0 && <div className="text-xs text-muted-foreground px-2">No chats yet.</div>}
      {conversations.map((c) => (
        <div key={c.conversation_id} onClick={() => onSelect(c.conversation_id)} data-testid={`chat-conv-${c.conversation_id}`}
          className={`group flex items-center justify-between rounded-md px-3 py-2 cursor-pointer transition-colors ${c.conversation_id === cid ? "bg-indigo-600/10" : "hover:bg-accent"}`}>
          <div className="min-w-0"><div className="text-sm font-medium truncate">{c.title}</div><div className="text-xs text-muted-foreground truncate">{c.last_message}</div></div>
          <button onClick={(e) => { e.stopPropagation(); onDelete(c.conversation_id); }} aria-label="Delete conversation" className="opacity-60 md:opacity-0 group-hover:opacity-100 transition-opacity">
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Assistant({ goTo }) {
  const [conversations, setConversations] = useState([]);
  const [cid, setCid] = useState(() => localStorage.getItem("sg_cid") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const loadConvs = () => api.get("/chat/conversations").then((r) => setConversations(r.data));
  useEffect(() => { loadConvs(); }, []);
  useEffect(() => {
    if (!cid) { setMessages([]); localStorage.removeItem("sg_cid"); return; }
    localStorage.setItem("sg_cid", cid);
    api.get(`/chat/conversations/${cid}`).then((r) => setMessages(r.data));
  }, [cid]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput(""); setSending(true);
    setMessages((m) => [...m, { role: "user", content: msg, id: `u-${Date.now()}` }]);
    try {
      const r = await api.post("/chat/send", { conversation_id: cid, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, id: `a-${Date.now()}` }]);
      if (r.data.conversation_id !== cid) setCid(r.data.conversation_id);
      loadConvs();
    } catch (e) {
      const m = formatErr(e.response?.data?.detail);
      if (e.response?.status === 402) toast.error(m, { action: { label: "Upgrade", onClick: () => goTo("settings") } });
      else toast.error(m);
    } finally { setSending(false); }
  };
  const del = async (c) => { await api.delete(`/chat/conversations/${c}`); if (c === cid) setCid(null); loadConvs(); };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="assistant-view">
      <div className="grid md:grid-cols-12 gap-6">
        <ConversationList conversations={conversations} cid={cid} onSelect={setCid} onNew={() => setCid(null)} onDelete={del} />
        <div className="md:col-span-8 border border-border rounded-xl bg-card h-[70vh] flex flex-col">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            <div className="font-display font-bold">ScopeGuard AI</div>
            <div className="text-xs text-muted-foreground hidden sm:block">· business copilot · remembers this conversation</div>
          </div>
          <MessageList messages={messages} sending={sending} testId="chat-messages"
            empty={<div className="space-y-3"><div>Your business copilot — pricing, contracts, client messages, change orders.</div>
              <div className="flex flex-col gap-2">{STARTERS.map((s, i) => (
                <button key={s} data-testid={`chat-starter-${i}`} onClick={() => send(s)} className="text-left text-sm border border-border rounded-lg px-3 py-2 hover:border-indigo-500/50 transition-colors text-foreground">{s}</button>
              ))}</div></div>} />
          <div className="p-4 border-t border-border flex gap-2">
            <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }} placeholder="Ask about pricing, contracts, or next steps…" />
            <Button data-testid="chat-send-btn" disabled={sending} onClick={() => send()} className="bg-indigo-600 hover:bg-indigo-500 text-white"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
